import { useAdjustBalance, useBulkResetAnnualBalances, useSearchBalances } from '@/api/balance/balance';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { RefreshCw, Search, Settings, Wallet } from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api-error';

export const BalanceManagementPage: React.FC = () => {
  const [searchUserId, setSearchUserId] = useState('');
  const [searchYear, setSearchYear] = useState(new Date().getFullYear());
  const [adjustmentDialog, setAdjustmentDialog] = useState<{
    open: boolean;
    userId?: string;
    email?: string | null;
    currentBalance?: number;
  }>({ open: false });
  const [bulkResetDialog, setBulkResetDialog] = useState(false);

  // Form states
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: '',
    description: '',
  });
  const [bulkResetForm, setBulkResetForm] = useState({
    year: new Date().getFullYear(),
    amount: 200,
  });

  const searchParams = useMemo(
    () => ({
      year: searchYear,
      limit: 50,
      offset: 0,
      user_id: searchUserId.trim() || undefined,
    }),
    [searchYear, searchUserId]
  );

  const {
    data: searchData,
    refetch: refetchSearch,
    isFetching,
    isLoading,
  } = useSearchBalances(searchParams, {
    query: {
      enabled: true, // Always fetch on mount and when params change
    },
  });

  const adjustMutation = useAdjustBalance();
  const bulkResetMutation = useBulkResetAnnualBalances();

  const handleSearch = () => {
    // Refetch with current search params
    refetchSearch();
  };

  const handleAdjustment = () => {
    if (!adjustmentDialog.userId || !adjustmentForm.amount || !adjustmentForm.description) {
      toast.error('Bitte füllen Sie alle erforderlichen Felder aus');
      return;
    }

    adjustMutation
      .mutateAsync({
        data: {
          user_id: adjustmentDialog.userId,
          amount: parseFloat(adjustmentForm.amount),
          description: adjustmentForm.description,
          year: searchYear,
        },
      })
      .then(() => {
        toast.success('Budgetanpassung erfolgreich');
        setAdjustmentDialog({ open: false });
        setAdjustmentForm({ amount: '', description: '' });
        handleSearch();
      })
      .catch(error => {
        toast.error(getApiErrorMessage(error, 'Anpassung des Budgets fehlgeschlagen'));
      });
  };

  const handleBulkReset = () => {
    bulkResetMutation
      .mutateAsync({
        data: {
          year: bulkResetForm.year,
          new_total_credit: bulkResetForm.amount,
        },
      })
      .then((data: { success_count?: number; error_count?: number }) => {
        toast.success(
          `Massen-Reset abgeschlossen: ${data.success_count || 0} erfolgreich, ${data.error_count || 0} Fehler`
        );
        setBulkResetDialog(false);
        handleSearch();
      })
      .catch(error => {
        toast.error(getApiErrorMessage(error, 'Massen-Reset fehlgeschlagen'));
      });
  };

  const balances = searchData?.balances || [];

  return (
    <div className='space-y-6'>
      {/* Page header (not a card) */}
      <section className='flex items-end justify-between gap-4'>
        <div className='flex flex-col gap-2'>
          <CardTitle className='text-swd-tuerkis-darker text-3xl leading-9 font-semibold'>Budgetverwaltung</CardTitle>
          <CardDescription className='text-neutral-grau tracking-wide'>
            Benutzerbudgets und Jahres-Resets verwalten
          </CardDescription>
        </div>
        <div className='flex items-end gap-3'>
          <AlertDialog open={bulkResetDialog} onOpenChange={setBulkResetDialog}>
            <AlertDialogTrigger asChild>
              <Button size='sm' variant='enabled'>
                Massen-Reset
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Massen-Reset Jahresbudgets</AlertDialogTitle>
                <AlertDialogDescription>
                  Setzt alle Benutzerbudgets für ein bestimmtes Jahr zurück. Alle Budgets werden auf den angegebenen
                  Betrag gesetzt.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className='py-4 space-y-4'>
                <div>
                  <Label htmlFor='resetYear'>Jahr</Label>
                  <Input
                    id='resetYear'
                    type='number'
                    value={bulkResetForm.year}
                    onChange={e =>
                      setBulkResetForm(prev => ({
                        ...prev,
                        year: parseInt(e.target.value),
                      }))
                    }
                    className='mt-2'
                  />
                </div>
                <div>
                  <Label htmlFor='resetAmount'>Neues Gesamtbudget (€)</Label>
                  <Input
                    id='resetAmount'
                    type='number'
                    step='0.01'
                    value={bulkResetForm.amount}
                    onChange={e =>
                      setBulkResetForm(prev => ({
                        ...prev,
                        amount: parseFloat(e.target.value),
                      }))
                    }
                    className='mt-2'
                  />
                </div>
                <Alert>
                  <AlertDescription>
                    <strong>Warnung:</strong> Diese Aktion setzt ALLE Benutzerbudgets für {bulkResetForm.year} auf €
                    {bulkResetForm.amount}
                    zurück. Dies kann nicht rückgängig gemacht werden.
                  </AlertDescription>
                </Alert>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={handleBulkReset} disabled={bulkResetMutation.isPending} variant='error'>
                  {bulkResetMutation.isPending && <RefreshCw className='h-4 w-4 animate-spin mr-2' />}
                  Alle Budgets zurücksetzen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </section>

      {/* Toolbar below header - filters */}
      <section className='flex items-center justify-between gap-3 flex-wrap rounded-xl border border-neutral-hellgrau bg-white px-3 py-4'>
        <div className='flex items-center gap-3 flex-wrap'>
          {/* Search */}
          <div className='relative flex-1 min-w-[200px]'>
            <Input
              placeholder='E-Mail eingeben...'
              value={searchUserId}
              onChange={e => setSearchUserId(e.target.value)}
              className='pr-8'
            />
            <Search className='absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none' />
          </div>

          {/* Year */}
          <div className='flex items-center gap-2'>
            <Label htmlFor='year' className='whitespace-nowrap text-neutral-dunkelgrau'>
              Jahr
            </Label>
            <Input
              id='year'
              type='number'
              value={searchYear}
              onChange={e => setSearchYear(parseInt(e.target.value))}
              className='w-24'
            />
          </div>

          <Button onClick={handleSearch} size='sm' variant='enabled' disabled={isFetching}>
            {isFetching ? <RefreshCw className='h-4 w-4 mr-2 animate-spin' /> : <Search className='h-4 w-4 mr-2' />}
            Suchen
          </Button>
        </div>
      </section>

      {/* Content area */}
      {isLoading || isFetching ? (
        <div className='text-center py-12 bg-white rounded-xl border border-neutral-hellgrau'>
          <RefreshCw className='h-8 w-8 text-swd-tuerkis mx-auto mb-4 animate-spin' />
          <p className='text-muted-foreground mb-4'>Lade Budgets...</p>
        </div>
      ) : !searchData ? (
        <div className='text-center py-8 bg-white rounded-xl border border-neutral-hellgrau'>
          <Wallet className='h-8 w-8 text-neutral-hellgrau mx-auto mb-4' />
          <p className='text-muted-foreground mb-4'>Verwenden Sie die Suchfelder, um Budgets zu finden</p>
        </div>
      ) : balances.length === 0 ? (
        <div className='text-center py-8 bg-white rounded-xl border border-neutral-hellgrau'>
          <Wallet className='h-8 w-8 text-neutral-hellgrau mx-auto mb-4' />
          <p className='text-muted-foreground mb-4'>Keine Budgets für die Suchkriterien gefunden</p>
        </div>
      ) : (
        <div className='rounded-xl border border-neutral-hellgrau overflow-hidden bg-white'>
          <Table className='min-w-[960px]'>
            <TableHeader className='bg-swd-hellblau'>
              <TableRow>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  E-Mail
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Jahr
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right'>
                  Gesamtbudget
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right'>
                  Verbraucht
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right'>
                  Verbleibend
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Nutzung
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap w-[100px] text-center'>
                  Aktionen
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {balances.map(
                (balance: {
                  user_id: string;
                  email?: string | null;
                  year: number;
                  total_credit: number;
                  used_credit: number;
                  remaining_credit: number;
                }) => {
                  const usagePercentage =
                    balance.total_credit > 0 ? (balance.used_credit / balance.total_credit) * 100 : 0;

                  return (
                    <TableRow
                      key={`${balance.user_id}-${balance.year}`}
                      className='hover:bg-swd-hellblau/40 border-b border-neutral-hellgrau'
                    >
                      <TableCell className='font-medium text-neutral-dunkelgrau align-middle text-sm'>
                        {balance.email || <span className='text-muted-foreground italic'>Keine E-Mail</span>}
                      </TableCell>
                      <TableCell className='align-middle font-medium text-neutral-grau'>{balance.year}</TableCell>
                      <TableCell className='text-right align-middle font-medium text-neutral-grau'>
                        €{Number(balance.total_credit).toFixed(2)}
                      </TableCell>
                      <TableCell className='text-right align-middle font-medium text-neutral-grau'>
                        €{Number(balance.used_credit).toFixed(2)}
                      </TableCell>
                      <TableCell className='text-right align-middle font-medium text-neutral-grau'>
                        €{Number(balance.remaining_credit).toFixed(2)}
                      </TableCell>
                      <TableCell className='align-middle'>
                        <div className='flex items-center gap-2'>
                          <div className='w-16 bg-neutral-hellgrau rounded-full h-2'>
                            <div
                              className={`h-2 rounded-full ${
                                usagePercentage > 80
                                  ? 'bg-red-500'
                                  : usagePercentage > 50
                                    ? 'bg-yellow-500'
                                    : 'bg-green-500'
                              }`}
                              style={{
                                width: `${Math.min(usagePercentage, 100)}%`,
                              }}
                            />
                          </div>
                          <span className='text-sm text-neutral-grau whitespace-nowrap'>
                            {usagePercentage.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className='text-center align-middle'>
                        <Button
                          variant='ghost'
                          size='sm'
                          className='h-8 w-8 p-0'
                          onClick={() =>
                            setAdjustmentDialog({
                              open: true,
                              userId: balance.user_id,
                              email: balance.email,
                              currentBalance: Number(balance.remaining_credit),
                            })
                          }
                        >
                          <Settings className='h-4 w-4 text-swd-tuerkis-darker' />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Adjustment Dialog */}
      <AlertDialog open={adjustmentDialog.open} onOpenChange={open => setAdjustmentDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzerbudget anpassen</AlertDialogTitle>
            <AlertDialogDescription>
              Budget für{' '}
              {adjustmentDialog.email || (
                <span className='italic text-muted-foreground'>{adjustmentDialog.userId?.slice(0, 8)}...</span>
              )}{' '}
              anpassen...
              <br />
              Aktuell verbleibendes Budget: €{adjustmentDialog.currentBalance?.toFixed(2)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='py-4 space-y-4'>
            <div>
              <Label htmlFor='adjustAmount'>Anpassungsbetrag (€)</Label>
              <Input
                id='adjustAmount'
                type='number'
                step='0.01'
                placeholder='Positiv zum Aufstocken, negativ zum Abziehen'
                value={adjustmentForm.amount}
                onChange={e =>
                  setAdjustmentForm(prev => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                className='mt-2'
              />
              <p className='text-xs text-muted-foreground mt-1'>
                Positive Zahlen erhöhen, negative Zahlen verringern das Budget
              </p>
            </div>
            <div>
              <Label htmlFor='adjustDescription'>Beschreibung *</Label>
              <Textarea
                id='adjustDescription'
                placeholder='Grund für die Budgetanpassung'
                value={adjustmentForm.description}
                onChange={e =>
                  setAdjustmentForm(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className='mt-2'
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleAdjustment} disabled={adjustMutation.isPending} variant='enabled'>
              {adjustMutation.isPending && <RefreshCw className='h-4 w-4 animate-spin mr-2' />}
              Anpassung anwenden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
